/**
 * The most a spot order may be for.
 *
 * Spot is simpler than perps — there is no leverage and no margin, just two
 * balances — but it has one rule that is easy to get wrong in the safe-looking
 * direction: **a BUY reserves nothing for the fee.** The fee is charged on the
 * asset RECEIVED, not on the quote spent (verified against the backend), so
 * holding a fee back would quietly shrink every "max" buy the trader made.
 *
 * The one thing a buy does reserve for is the `maxQuoteIn` cap on the M-1
 * chains: a market buy's cap is quote-in x (1 + slippage), so the balance has
 * to cover the INFLATED figure or the order reverts before it fills. That only
 * applies to a market buy on a chain whose router carries the parameter.
 *
 * Sizing is ported from `resolveSpotOrderSize`: always floored to the market's
 * step — a BUY must never spend more quote than entered, a SELL never sell more
 * base (a 2.6 USDC buy once rounded UP to 0.0011 ETH and cost $2.72) — and when
 * flooring leaves less than the market's minimum the maximum is unavailable
 * rather than rounded back up.
 */
import { BookLevels } from "../book";
import { Dec, dec } from "../decimal";
import { MarketSteps } from "../spec";
import { applyOrderSizeLimits, resolveOrderPricer } from "./perpOrderMax";
import { MaxResult, maxOk, maxUnavailable } from "./types";

const SIZE_DECIMALS = 18;
const QUOTE_DECIMALS = 2;

export interface SpotOrderMaxInput {
  side: "buy" | "sell";
  orderType: "market" | "limit";
  /** Which terms the field is in: the base asset, or the quote token. */
  unit: "base" | "quote";
  /** The base token the trader holds — what a SELL spends. */
  baseAvailable?: Dec | null;
  /** The quote token the trader holds — what a BUY spends. */
  quoteAvailable?: Dec | null;
  limitPrice?: Dec;
  book?: { bids?: BookLevels; asks?: BookLevels };
  mark?: Dec;
  steps: MarketSteps;
  /** The trader's slippage tolerance, for the `maxQuoteIn` cap. */
  slippageBps?: number;
  /** True when this chain's router caps the quote spent on a market buy. */
  hasMaxQuoteIn?: boolean;
  baseSymbol?: string;
  quoteSymbol?: string;
}

const positive = (value: Dec | number | null | undefined): Dec | null => {
  if (value === null || value === undefined) return null;
  const parsed = dec.parse(value);
  return parsed !== null && dec.gt(parsed, "0") ? parsed : null;
};

const clampBps = (bps: number | undefined): number => {
  if (bps === undefined || !Number.isFinite(bps) || bps <= 0) return 0;
  return bps > 10000 ? 10000 : bps;
};

/** A balance that is known and positive, or the reason it is not usable. */
const readBalance = (
  balance: Dec | null | undefined
): { ok: true; value: Dec } | { ok: false; result: MaxResult } => {
  const parsed = dec.parse(balance ?? null);
  // Unknown is not empty: "balance_unknown" says wait, "zero_balance" says
  // deposit. Showing a loading balance as 0 told the trader the wrong one.
  if (parsed === null) return { ok: false, result: maxUnavailable("balance_unknown") };
  if (!dec.gt(parsed, "0")) {
    return { ok: false, result: maxUnavailable("zero_balance") };
  }
  return { ok: true, value: parsed };
};

export const spotOrderMax = (input: SpotOrderMaxInput): MaxResult => {
  const { steps } = input;
  if (positive(steps.amountStep) === null) return maxUnavailable("specs_unknown");

  const baseUnit = input.baseSymbol ?? "";
  const quoteUnit = input.quoteSymbol ?? "USDC";

  if (input.side === "sell") {
    const balance = readBalance(input.baseAvailable);
    if (!balance.ok) return balance.result;
    // A sale is already in base terms: no price is needed to size it, only to
    // show it in quote terms.
    const sized = applyOrderSizeLimits(balance.value, steps, "balance", baseUnit);
    return input.unit === "quote" ? inQuote(sized, null, input, quoteUnit) : sized;
  }

  const balance = readBalance(input.quoteAvailable);
  if (!balance.ok) return balance.result;

  const spendable = reserveForQuoteCap(balance.value, input);
  const pricer = resolveOrderPricer(input);
  if (!pricer) return maxUnavailable("price_unknown");

  const sized = applyOrderSizeLimits(
    pricer.toBase(spendable),
    steps,
    "balance",
    baseUnit
  );
  return input.unit === "quote" ? inQuote(sized, spendable, input, quoteUnit) : sized;
};

/**
 * The quote a market BUY may actually name, once the router's cap is allowed
 * for. `maxQuoteIn` is quote-in x (1 + slippage), so the trader can only enter
 * `balance / (1 + slippage)` without the cap exceeding what they hold.
 *
 * With no tolerance set the cap is `uint256.max` — no cap — which is what the
 * spot form sends today, so nothing is reserved.
 */
const reserveForQuoteCap = (balance: Dec, input: SpotOrderMaxInput): Dec => {
  if (!input.hasMaxQuoteIn || input.orderType !== "market") return balance;
  const bps = clampBps(input.slippageBps);
  if (bps === 0) return balance;
  const divisor = dec.add("1", dec.bps("1", bps, "ceil"));
  return dec.div(balance, divisor, SIZE_DECIMALS, "floor") ?? balance;
};

/**
 * The same maximum in quote terms, floored to cents.
 *
 * Bounded by what the trader can actually spend on a BUY: beyond the book's
 * depth the value of a size and the money behind it diverge, and the smaller of
 * the two is the honest one. A SELL has no such bound — its balance is the base
 * side — so it is valued outright.
 */
const inQuote = (
  sized: MaxResult,
  spendable: Dec | null,
  input: SpotOrderMaxInput,
  unit: string
): MaxResult => {
  if (!sized.ok) return sized;
  const pricer = resolveOrderPricer(input);
  if (!pricer) return maxUnavailable("price_unknown");

  const valued = pricer.toQuote(sized.max);
  const quote = spendable === null ? valued : dec.min(valued, spendable);
  const floored = dec.round(quote, QUOTE_DECIMALS, "floor");
  if (!dec.gt(floored, "0")) {
    return maxUnavailable("below_min_order", input.steps.minOrderAmount);
  }
  return maxOk(floored, sized.limitedBy, unit);
};
